import { AnimatePresence } from 'motion/react';
import React, {
    ChangeEventHandler,
    CSSProperties,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import Icon from '../icon/Icon';
import Input, { InputRef, InputSize } from '../input/Input';
import {
    StyledMotionSearchInputContentWrapper,
    StyledMotionSearchInputIconWrapper,
    StyledMotionSearchInputIconWrapperContent,
    StyledSearchInput,
    StyledSearchInputIconTrigger,
    StyledSearchInputPseudoElement,
} from './SearchInput.styles';
import { useTheme } from 'styled-components';
import type { Theme } from '../color-scheme-provider/ColorSchemeProvider';
import { useElementSize } from '../../hooks/element';
import { useKeyboardFocusHighlighting } from '../../hooks/useKeyboardFocusHighlighting';

export type SearchInputProps = {
    /**
     * Color of the icon
     */
    iconColor?: CSSProperties['color'];
    /**
     * Force the active state of the input and override the internal state
     */
    isActive?: boolean;
    /**
     * Function that is executed when the active state of the input changes
     */
    onActiveChange?: (isActive: boolean) => void;
    /**
     * Function that is executed when the text of the input changes
     */
    onChange: ChangeEventHandler<HTMLInputElement>;
    /**
     * Function that is executed when a key is pressed
     */
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    /**
     * Placeholder for the input field
     */
    placeholder?: string;
    /**
     * Whether the SearchInput should be positioned absolute.
     */
    shouldUseAbsolutePositioning?: boolean;
    /**
     * The size of the input field
     */
    size?: InputSize;
    /**
     * Value if the input field should be controlled
     */
    value?: string;
    /**
     * Enables keyboard-only focus highlighting.
     */
    shouldEnableKeyboardHighlighting?: boolean;
    /**
     * The width of the parent.
     */
    width?: number;
};

const SearchInput = forwardRef<InputRef, SearchInputProps>(
    (
        {
            iconColor,
            isActive,
            onActiveChange,
            onChange,
            onKeyDown,
            placeholder,
            shouldUseAbsolutePositioning = false,
            size = InputSize.Medium,
            value,
            shouldEnableKeyboardHighlighting,
            width: widthValue,
        },
        ref,
    ) => {
        const [isSearchInputActive, setIsSearchInputActive] = useState(
            isActive ?? (typeof value === 'string' && value.trim() !== ''),
        );

        const inputRef = useRef<InputRef>(null);
        const pseudoRef = useRef<HTMLDivElement>(null);
        const iconTriggerRef = useRef<HTMLDivElement>(null);

        const parentWidth = useElementSize(pseudoRef);

        const theme = useTheme() as Theme;

        const shouldShowKeyboardHighlighting = useKeyboardFocusHighlighting(
            shouldEnableKeyboardHighlighting,
        );

        const handleBackIconClick = useCallback(() => setIsSearchInputActive(false), []);

        const handleSearchIconClick = useCallback(() => setIsSearchInputActive(true), []);

        const handleIconTriggerClick = useCallback(() => {
            if (isSearchInputActive) {
                handleBackIconClick();
                return;
            }

            handleSearchIconClick();
        }, [handleBackIconClick, handleSearchIconClick, isSearchInputActive]);

        const handleIconTriggerKeyDown = useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleIconTriggerClick();
                }
            },
            [handleIconTriggerClick],
        );

        const handleInputKeyDown = useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    setIsSearchInputActive(false);
                    iconTriggerRef.current?.focus();
                }

                if (typeof onKeyDown === 'function') {
                    onKeyDown(event);
                }
            },
            [onActiveChange, onKeyDown],
        );

        useEffect(() => {
            if (typeof onActiveChange === 'function') {
                onActiveChange(isSearchInputActive);
            }
        }, [isSearchInputActive, onActiveChange]);

        useEffect(() => {
            if (isSearchInputActive) {
                inputRef.current?.focus();
            }
        }, [isSearchInputActive]);

        useEffect(() => {
            if (typeof isActive === 'boolean') {
                setIsSearchInputActive(isActive);
            }
        }, [isActive]);

        useImperativeHandle(
            ref,
            () => ({
                focus: () => inputRef.current?.focus(),
                blur: () => inputRef.current?.blur(),
            }),
            [],
        );

        const width = useMemo(
            () => widthValue ?? parentWidth?.width,
            [parentWidth?.width, widthValue],
        );

        return (
            <>
                <StyledSearchInput
                    className="beta-chayns-search-input"
                    $size={size}
                    $shouldUseAbsolutePositioning={shouldUseAbsolutePositioning}
                >
                    {shouldUseAbsolutePositioning ? (
                        <AnimatePresence initial={false}>
                            {isSearchInputActive && (
                                <StyledMotionSearchInputContentWrapper
                                    $shouldUseAbsolutePositioning={shouldUseAbsolutePositioning}
                                    $shouldShowKeyboardHighlighting={shouldShowKeyboardHighlighting}
                                    animate={{ opacity: 1, width }}
                                    exit={{ opacity: 0, width: 0 }}
                                    initial={{ opacity: 0, width: 0 }}
                                    key="searchInputContentWrapper"
                                    transition={{ duration: 0.25, type: 'tween' }}
                                >
                                    <Input
                                        onChange={onChange}
                                        onKeyDown={handleInputKeyDown}
                                        placeholder={placeholder}
                                        ref={inputRef}
                                        shouldEnableKeyboardHighlighting={
                                            shouldEnableKeyboardHighlighting
                                        }
                                        shouldShowClearIcon
                                        size={size}
                                        value={value}
                                    />
                                </StyledMotionSearchInputContentWrapper>
                            )}
                            <StyledSearchInputIconTrigger
                                ref={iconTriggerRef}
                                id={
                                    isSearchInputActive
                                        ? 'search-input-backIcon'
                                        : 'search-input-searchIcon'
                                }
                                tabIndex={0}
                                onClick={handleIconTriggerClick}
                                onKeyDown={handleIconTriggerKeyDown}
                                $shouldShowKeyboardHighlighting={shouldShowKeyboardHighlighting}
                            >
                                <StyledMotionSearchInputIconWrapperContent
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, position: 'absolute' }}
                                    initial={{ opacity: 0 }}
                                    key={isSearchInputActive ? 'backIcon' : 'searchIcon'}
                                    transition={{ duration: 0.3 }}
                                    $shouldShowKeyboardHighlighting={shouldShowKeyboardHighlighting}
                                >
                                    <Icon
                                        key="icon"
                                        color={iconColor}
                                        tabIndex={-1}
                                        icons={
                                            isSearchInputActive ? ['fa fa-xmark'] : ['fa fa-search']
                                        }
                                    />
                                </StyledMotionSearchInputIconWrapperContent>
                            </StyledSearchInputIconTrigger>
                        </AnimatePresence>
                    ) : (
                        <>
                            <StyledMotionSearchInputIconWrapper>
                                <AnimatePresence initial={false}>
                                    <StyledSearchInputIconTrigger
                                        ref={iconTriggerRef}
                                        id={
                                            isSearchInputActive
                                                ? 'search-input-backIcon'
                                                : 'search-input-searchIcon'
                                        }
                                        tabIndex={0}
                                        onClick={handleIconTriggerClick}
                                        onKeyDown={handleIconTriggerKeyDown}
                                        $shouldShowKeyboardHighlighting={
                                            shouldShowKeyboardHighlighting
                                        }
                                    >
                                        <StyledMotionSearchInputIconWrapperContent
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0, position: 'absolute' }}
                                            initial={{ opacity: 0 }}
                                            key={isSearchInputActive ? 'backIcon' : 'searchIcon'}
                                            transition={{ duration: 0.3 }}
                                            $shouldShowKeyboardHighlighting={
                                                shouldShowKeyboardHighlighting
                                            }
                                        >
                                            <Icon
                                                key="icon"
                                                color={iconColor}
                                                tabIndex={-1}
                                                icons={
                                                    isSearchInputActive
                                                        ? ['fa fa-arrow-left']
                                                        : ['fa fa-search']
                                                }
                                            />
                                        </StyledMotionSearchInputIconWrapperContent>
                                    </StyledSearchInputIconTrigger>
                                </AnimatePresence>
                            </StyledMotionSearchInputIconWrapper>
                            <AnimatePresence initial={false}>
                                {isSearchInputActive && (
                                    <StyledMotionSearchInputContentWrapper
                                        $shouldUseAbsolutePositioning={shouldUseAbsolutePositioning}
                                        $shouldShowKeyboardHighlighting={
                                            shouldShowKeyboardHighlighting
                                        }
                                        animate={{ opacity: 1, width: '100%' }}
                                        exit={{ opacity: 0, width: 0 }}
                                        initial={{ opacity: 0, width: 0 }}
                                        key="searchInputContentWrapper"
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Input
                                            key="input"
                                            leftElement={
                                                <Icon
                                                    color={theme.text}
                                                    icons={['far fa-search']}
                                                />
                                            }
                                            onChange={onChange}
                                            onKeyDown={handleInputKeyDown}
                                            placeholder={placeholder}
                                            ref={inputRef}
                                            shouldEnableKeyboardHighlighting={
                                                shouldEnableKeyboardHighlighting
                                            }
                                            shouldShowClearIcon
                                            size={size}
                                            value={value}
                                        />
                                    </StyledMotionSearchInputContentWrapper>
                                )}
                            </AnimatePresence>
                        </>
                    )}
                </StyledSearchInput>
                <StyledSearchInputPseudoElement ref={pseudoRef} />
            </>
        );
    },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
